import React, { useState } from 'react';
import { User, UserPermissions } from '../../types';
import { countEnabledSettingsTabs } from '../../utils/permissions';
import { ZoomControl } from './ZoomControl';

export interface UserMenuProps {
  currentUser?: User | null;
  userPermissions?: UserPermissions;
  zoomLevel: number;
  onZoomChange: (zoom: number) => void;
  onOpenSettings?: () => void;
  onOpenChangePassword?: () => void;
  onLogout?: () => void;
}

/**
 * Retorna iniciais do nome de usuário para o avatar.
 * @param name - Nome ou username
 * @returns Uma letra maiúscula
 */
function getInitials(name: string) {
  if (!name || name.trim().length === 0) return '?';
  const parts = name.trim().split(/\s+/);
  return parts[0][0].toUpperCase();
}

/**
 * Menu do perfil: zoom, trocar senha, configurações (admin) e logout.
 * @param props - Usuário logado e callbacks de ação
 */
export const UserMenu: React.FC<UserMenuProps> = ({
  currentUser,
  userPermissions,
  zoomLevel,
  onZoomChange,
  onOpenSettings,
  onOpenChangePassword,
  onLogout,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const username = currentUser?.username || 'Usuário';
  const displayRole = !currentUser?.permissions.settings
    ? 'Colaborador'
    : countEnabledSettingsTabs(currentUser.permissions.settingsTabs) >= 9
      ? 'Administrador'
      : 'Config. parcial';
  const initials = getInitials(username);

  return (
    <div className="pl-0.5 relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`size-7 rounded-full flex items-center justify-center bg-secondary-accent text-white text-[10px] font-bold ring-2 shadow-md cursor-pointer hover:scale-105 transition-all ${isOpen ? 'ring-secondary-accent scale-105' : 'ring-white'}`}
        aria-label="Menu do usuário"
        aria-expanded={isOpen}
      >
        {initials}
      </button>

      {isOpen && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} aria-hidden="true" />
          <div className="absolute right-0 top-full mt-2 w-40 bg-white rounded-xl shadow-[0_10px_40px_-10px_rgba(0,0,0,0.15)] border border-slate-100 p-1.5 z-50 animate-in fade-in zoom-in-95 duration-200 origin-top-right">
            <div className="px-2.5 py-1.5 mb-1 border-b border-slate-50">
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">{displayRole}</p>
              <p className="text-[11px] font-semibold text-slate-700 truncate capitalize">{username}</p>
            </div>

            <ZoomControl zoomLevel={zoomLevel} onZoomChange={onZoomChange} />

            <button
              type="button"
              onClick={() => {
                setIsOpen(false);
                onOpenChangePassword?.();
              }}
              className="flex items-center w-full gap-2 px-2.5 py-1.5 rounded-lg text-[11px] font-medium text-slate-600 hover:bg-slate-50 transition-colors group"
            >
              <span className="material-symbols-outlined !text-[14px] text-slate-400 group-hover:text-primary-accent transition-colors">
                key
              </span>
              Trocar Senha
            </button>

            {userPermissions?.settings && (
              <button
                type="button"
                onClick={() => {
                  setIsOpen(false);
                  onOpenSettings?.();
                }}
                className="flex items-center w-full gap-2 px-2.5 py-1.5 rounded-lg text-[11px] font-medium text-slate-600 hover:bg-slate-50 transition-colors group"
              >
                <span className="material-symbols-outlined !text-[14px] text-slate-400 group-hover:text-primary-accent transition-colors">
                  settings
                </span>
                Configurações
              </button>
            )}
            <button
              type="button"
              onClick={() => {
                setIsOpen(false);
                onLogout?.();
              }}
              className="flex items-center w-full gap-2 px-2.5 py-1.5 rounded-lg text-[11px] font-medium text-red-500 hover:bg-red-50 transition-colors group"
            >
              <span className="material-symbols-outlined !text-[14px]">logout</span>
              Sair
            </button>
          </div>
        </>
      )}
    </div>
  );
};
